import { Injectable } from '@angular/core';
import { ITableColumn } from 'src/app/models/tableColumns';
import { TABLE_COLUMNS_VERSION, TableColumnsNames } from 'src/app/models/tableColumns/tableColumnsNames.enum';

interface StoredColumnsData {
  version: number;
  columns: ITableColumn[];
}

@Injectable({
  providedIn: 'root',
})
export class ColumnsOrderService {
  getColumnsOrderFromLocalStorage(pageName: TableColumnsNames, defaultColumns: ITableColumn[]): ITableColumn[] {
    const stored = localStorage.getItem(`${pageName}_columns`);
    if (!stored) return defaultColumns;

    try {
      const data = JSON.parse(stored) as StoredColumnsData | ITableColumn[];

      // Handle legacy format (array without version)
      if (Array.isArray(data)) {
        const mergedColumns = this._mergeColumnsWithDefaults(data, defaultColumns);
        this.saveColumnsOrderToLocalStorage(pageName, mergedColumns);
        return mergedColumns;
      }

      if (data.version !== TABLE_COLUMNS_VERSION) {
        const mergedColumns = this._mergeColumnsWithDefaults(data.columns, defaultColumns);
        this.saveColumnsOrderToLocalStorage(pageName, mergedColumns);
        return mergedColumns;
      }

      return data.columns;
    } catch {
      return defaultColumns;
    }
  }

  saveColumnsOrderToLocalStorage(pageName: TableColumnsNames, columns: ITableColumn[]): void {
    const data: StoredColumnsData = { version: TABLE_COLUMNS_VERSION, columns };
    localStorage.setItem(`${pageName}_columns`, JSON.stringify(data));
  }

  private _mergeColumnsWithDefaults(cached: ITableColumn[], defaults: ITableColumn[]): ITableColumn[] {
    const cachedMap = new Map(cached.map((col, index) => [col.propertyKey, { index, hidden: col.hidden }]));

    const result = defaults.map((defaultCol) => ({
      ...defaultCol,
      hidden: cachedMap.get(defaultCol.propertyKey)?.hidden ?? defaultCol.hidden,
    }));

    result.sort((a, b) => {
      const aIndex = cachedMap.get(a.propertyKey)?.index ?? Infinity;
      const bIndex = cachedMap.get(b.propertyKey)?.index ?? Infinity;
      return aIndex - bIndex;
    });

    return result;
  }
}
